import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "src/hooks/selectors/useAuth";
import useElementSize from "src/hooks/useElementSize";
import Button from "src/components/Button";
import FlatList from "src/components/FlatList";
import TaskTableRow from "src/components/tasks/TaskTableRow";
import useTasks from "src/hooks/api/services/tasks/useTasks";
import useModules from "src/hooks/selectors/useModules";
import { useUpdateEffectDebounce } from "src/hooks/useDebounce";
import useTasksFilter from "src/hooks/api/tasks/useTasksFilter";

interface TasksTableProps {
	filters?: any;
	setModalTask: (hid: string) => void;
	searchActions?: any;
	options?: {
		scrollToTop?: boolean;
	};
}

const TasksTable = ({
	setModalTask,
	searchActions,
	options,
}: TasksTableProps) => {
	const { modules } = useModules();
	const { t } = useTranslation("tasks", {
		keyPrefix: "tasks-table",
	});
	const auth = useAuth();
	const type = auth.type;
	const { ref } = useElementSize();
	const width = useRef() as any;
	const { tasks, status, pagination, actions } = useTasks();
	const { filter } = useTasksFilter();
	useUpdateEffectDebounce(filter, 1000, () => {
		actions.list(filter);
	});
	const loading = status === "loading" && tasks.length === 0;
	const headers = [
		t("headers.status"),
		t("headers.task"),
		t("headers.registrations"),
		t("headers.budget"),
		t("headers.date"),
		t("headers.type"),
		t("headers.actions"),
	];

	useEffect(() => {
		actions.list({
			variant: "table",
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!modules?.includes("extern-task")) {
		headers.splice(headers.indexOf(t("headers.type")), 1);
	}

	const texts = {
		title:
			type === "student"
				? t("empty.student.title")
				: t("empty.company.title"),
		text:
			type === "student"
				? t("empty.student.text")
				: t("empty.company.text"),
	};

	const setPage = (page: number) => {
		pagination.setPage(page);
		if (options?.scrollToTop && ref?.current) {
			const pos = ref?.current.getBoundingClientRect();
			const y = pos.top + window.pageYOffset - 140;
			window.scrollTo({ top: y, behavior: "smooth" });
		}
	};

	if (tasks?.length === 0 && !loading && type === "company" && !filter) {
		return (
			<div className="flex flex-1 flex-col gap-4 justify-center items-center text-center">
				<h2>{t("empty.company.title")}</h2>
				<p className="text-center">{t("empty.company.text")}</p>
				<Button
					className="mt-4"
					to={
						auth.has_subscription
							? "/tasks/create"
							: "/subscription"
					}
				>
					{t("empty.company.button")}
				</Button>
			</div>
		);
	}

	return (
		<div
			{...{ ref }}
			style={{ maxWidth: width.current }}
			className="flex flex-col gap-4"
		>
			<FlatList
				variant="flat"
				empty={texts as any}
				table={{
					headers,
				}}
				{...{
					loading,
				}}
				fetching={status === "loading"}
				data={tasks}
				pagination={{
					...pagination,
					setPage,
				}}
				renderLoadingItem={(index) => (
					<TaskTableRow.Loading key={`loading-${index}`} />
				)}
				renderItem={(task) => (
					<TaskTableRow
						{...{ task, setModalTask }}
						key={`task-${task.hid}`}
					/>
				)}
			/>
		</div>
	);
};

export default TasksTable;
